import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Field } from '../components/Field';
import { ViewHeader } from '../components/ViewHeader';
import { EntityRow } from '../components/EntityRow';
import { EmptyState } from '../components/EmptyState';
import { useWorkspace } from '../hooks/useWorkspace';
import { useLang } from '../hooks/useLang';
import { useEntityList } from '../hooks/useEntityList';
import { useToast } from '../components/Toast';
import { LIMITS, NATIONS } from '../../domain/constants';
import { athleteName, newAthlete } from '../../domain/workspace';
import { t } from '../../i18n';
import type { WorkspaceStore } from '../../domain/store';
import type { Athlete } from '../../types';

function sexOptions(): ReadonlyArray<readonly [string, string]> {
  return [
    ['w', t('common.sexWoman')],
    ['m', t('common.sexMan')],
  ];
}

export function AthletesView({ store, hidden }: { store: WorkspaceStore; hidden: boolean }) {
  const workspace = useWorkspace(store);
  useLang();
  const toast = useToast();
  const { editing, startEditing, stopEditing, stopEditingIf } = useEntityList();
  const { athletes, clubs } = workspace;

  function handleAdd(): void {
    // `meta.club` (the submitting club, free text) is only used to guess a
    // starting selection — matched against the Vereine list by name; no
    // match just leaves the new athlete's club unset.
    const defaultClub = store.workspace.clubs.find((c) => c.name === store.workspace.meta.club);
    const a = newAthlete(defaultClub?.id ?? '');
    store.update((p) => p.athletes.push(a));
    startEditing(a.id);
  }

  function handleDelete(id: string): void {
    const used = store.workspace.starters.filter((s) => s.athleteIds.includes(id));
    if (used.length > 0 && !confirm(t('athletes.deleteConfirm', { count: used.length }))) return;
    store.update((p) => {
      p.athletes = p.athletes.filter((a) => a.id !== id);
      for (const s of p.starters) s.athleteIds = s.athleteIds.filter((x) => x !== id);
    });
    stopEditingIf(id);
    toast(t('athletes.deleted'));
  }

  return (
    <section className="mx-auto flex max-w-[1180px] flex-col gap-3 p-4" hidden={hidden}>
      <ViewHeader title={t('athletes.title')} lede={t('athletes.lede')} />

      {athletes.length === 0 ? (
        <EmptyState title={t('athletes.empty.title')} text={t('athletes.empty.text')} />
      ) : (
        athletes.map((a) =>
          a.id === editing ? (
            <AthleteForm
              key={a.id}
              a={a}
              clubs={clubs}
              store={store}
              onDone={stopEditing}
              onDelete={() => handleDelete(a.id)}
            />
          ) : (
            <AthleteRow key={a.id} a={a} onEdit={() => startEditing(a.id)} onDelete={() => handleDelete(a.id)} />
          ),
        )
      )}
      <Button className="w-full" onClick={handleAdd}>
        {t('athletes.add')}
      </Button>
    </section>
  );
}

function AthleteRow({ a, onEdit, onDelete }: { a: Athlete; onEdit: () => void; onDelete: () => void }) {
  const year = a.birthDate ? a.birthDate.slice(0, 4) : '—';
  const sex = a.sex === 'w' ? t('common.sexWoman') : t('common.sexMan');
  return (
    <EntityRow
      title={athleteName(a)}
      sub={`${a.uciId || t('common.uciIdMissing')} · ${year} · ${a.nation} · ${sex}`}
      actions={
        <>
          <Button variant="outline" size="sm" onClick={onEdit}>
            {t('common.edit')}
          </Button>
          <Button variant="destructive" size="sm" onClick={onDelete}>
            {t('common.delete')}
          </Button>
        </>
      }
    />
  );
}

function AthleteForm({
  a,
  clubs,
  store,
  onDone,
  onDelete,
}: {
  a: Athlete;
  clubs: WorkspaceStore['workspace']['clubs'];
  store: WorkspaceStore;
  onDone: () => void;
  onDelete: () => void;
}) {
  const clubOptions: Array<readonly [string, string]> = [['', t('athletes.club.none')], ...clubs.map((c) => [c.id, c.name] as const)];
  return (
    <Card className="ring-2 ring-ring/40">
      <CardHeader>
        <CardTitle>{t('athletes.form.title')}</CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field scope="athlete" store={store} target={a} spec={{ label: t('field.firstName'), key: 'firstName', maxLength: LIMITS.name, required: true }} />
        <Field scope="athlete" store={store} target={a} spec={{ label: t('field.lastName'), key: 'lastName', maxLength: LIMITS.name, required: true }} />
        <Field scope="athlete" store={store} target={a} spec={{ label: t('field.uciId'), key: 'uciId', maxLength: LIMITS.uciId, mono: true, required: true }} />
        <Field scope="athlete" store={store} target={a} spec={{ label: t('athletes.field.birthDate'), key: 'birthDate', type: 'date', required: true, hint: '' }} />
        <Field
          scope="athlete"
          store={store}
          target={a}
          commit={false}
          spec={{ label: t('athletes.field.nation'), key: 'nation', required: true, options: NATIONS.map((n) => [n, n] as const) }}
        />
        <Field
          scope="athlete"
          store={store}
          target={a}
          commit={false}
          spec={{ label: t('athletes.field.sex'), key: 'sex', required: true, options: sexOptions() }}
        />
        <Field
          scope="athlete"
          store={store}
          target={a}
          commit={false}
          spec={{ label: t('athletes.field.club'), key: 'clubId', options: clubOptions, hint: clubs.length === 0 ? t('athletes.club.emptyHint') : '' }}
        />
        <Field scope="athlete" store={store} target={a} spec={{ label: t('field.email'), key: 'email', maxLength: LIMITS.email, type: 'email' }} />
        <Field scope="athlete" store={store} target={a} spec={{ label: t('field.street'), key: 'street', maxLength: LIMITS.street }} />
        <Field scope="athlete" store={store} target={a} spec={{ label: t('field.phone'), key: 'phone', maxLength: LIMITS.phone, type: 'tel' }} />
        <Field scope="athlete" store={store} target={a} spec={{ label: t('field.postalCode'), key: 'postalCode', maxLength: LIMITS.postalCode, mono: true }} />
        <Field scope="athlete" store={store} target={a} spec={{ label: t('field.city'), key: 'city', maxLength: LIMITS.city }} />
        <Field scope="athlete" store={store} target={a} spec={{ label: t('field.countryCode'), key: 'countryCode', maxLength: LIMITS.countryCode, mono: true }} />
        <Field scope="athlete" store={store} target={a} spec={{ label: t('field.fax'), key: 'fax', maxLength: LIMITS.fax }} />
      </CardContent>
      <CardFooter>
        <Button onClick={onDone}>{t('common.done')}</Button>
        <Button variant="destructive" onClick={onDelete}>
          {t('common.delete')}
        </Button>
      </CardFooter>
    </Card>
  );
}
